import { readApiJson } from '../utils/readApiJson';

const API_BASE = '/api/cyber/offers';

export interface CyberProspect {
  id: string;
  prismeClientId?: number | null;
  organizationName: string;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  status: string;
  ownerUserId?: number | null;
  notes?: string | null;
  proposalCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CyberProspectInput {
  organizationName: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  notes?: string;
}

export interface CyberQuestion {
  id: string;
  code: string;
  label: string;
  helpText?: string | null;
  answerType: 'text' | 'number' | 'boolean' | 'choice' | 'multi_choice';
  unit?: string | null;
  options?: { label: string; value: string | number | boolean }[];
  required: boolean;
  questionScope: 'always' | 'service_specific' | 'admin' | 'budget_diagnostic' | 'nist_full';
  serviceCodes: string[];
  sectionCode?: string | null;
  sectionName?: string | null;
  displayOrder: number;
}

export interface CyberService {
  id: string;
  code: string;
  name: string;
  categoryName?: string | null;
  serviceType: string;
  status: string;
  clientDescription?: string | null;
}

export interface CyberProposal {
  id: string;
  prospectId: string;
  organizationName: string;
  status: string;
  subtotalOneTime: number;
  subtotalRecurringMonthly: number;
  discountTotal: number;
  finalOneTimeTotal: number;
  finalRecurringMonthlyTotal: number;
  calculationTrace?: Record<string, unknown>;
  advisorNotes?: string | null;
  archivedAt?: string | null;
  archivedReason?: string | null;
  archivedBy?: number | null;
  sentAt?: string | null;
  sentBy?: number | null;
  sentProvider?: string | null;
  sentMessageId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CyberProposalActivity {
  id: string;
  proposalId: string;
  action: string;
  label: string;
  detail?: string | null;
  actorUserId?: number | null;
  actorInitials?: string | null;
  actorName?: string | null;
  actorEmail?: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface CyberAnswer {
  id?: string;
  questionId: string;
  questionCode?: string;
  value?: string | number | boolean | null;
  valueText?: string | null;
  valueNumber?: number | null;
  valueBoolean?: boolean | null;
  valueJson?: unknown;
}

export interface CyberProspectDetail {
  prospect: CyberProspect;
  answers: CyberAnswer[];
  proposals: CyberProposal[];
  archivedProposals?: CyberProposal[];
  activities?: CyberProposalActivity[];
}

export interface CyberProposalLineItem {
  id: string;
  serviceCode: string;
  serviceName: string;
  categoryName?: string | null;
  clientDescription?: string | null;
  recommendationReason?: string;
  selectionSource?: string;
  selected: boolean;
  pricingModel?: string;
  roundedAmount: number;
  recurringAmount: number;
  calculatedAmount?: number;
  calculationTrace?: Record<string, any>;
}

export interface CyberProposalDiscount {
  id: string;
  discountType: string;
  percent: number;
  amount: number;
  reason?: string | null;
  approvalStatus?: string;
  createdAt?: string;
}

export interface CyberProposalOutput {
  id: string;
  outputType: string;
  title: string;
  htmlSnapshot?: string | null;
  status: string;
  createdAt?: string;
}

export interface CyberGeneratedProposal {
  proposal: CyberProposal;
  lineItems: CyberProposalLineItem[];
  discounts?: CyberProposalDiscount[];
  outputs?: CyberProposalOutput[];
  activities?: CyberProposalActivity[];
  answers?: CyberAnswer[];
  clientVisualHtml: string;
}

export interface CyberOfferEmailResult {
  sent: boolean;
  draft?: boolean;
  draftId?: string | null;
  sentTo: string;
  subject: string;
  provider?: 'smtp' | 'gmail' | string;
  from?: string | null;
  messageId?: string | null;
  outputs: CyberProposalOutput[];
  detail: CyberGeneratedProposal;
}

export interface CyberAdminSnapshot {
  services: any[];
  categories: any[];
  descriptions: any[];
  deliverables: any[];
  parameterSets: any[];
  parameters: any[];
  calculationRules: any[];
  recommendationRules: any[];
  discountRules: any[];
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  const data = await readApiJson<T | { error?: string }>(response);
  if (!response.ok) {
    throw new Error((data as { error?: string })?.error || `Erreur API Offres cyber (${response.status})`);
  }
  return data as T;
}

export function getCyberProspects(): Promise<CyberProspect[]> {
  return requestJson<CyberProspect[]>('/prospects');
}

export function createCyberProspect(input: CyberProspectInput): Promise<CyberProspect> {
  return requestJson<CyberProspect>('/prospects', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getCyberProspectDetail(id: string): Promise<CyberProspectDetail> {
  return requestJson<CyberProspectDetail>(`/prospects/${encodeURIComponent(id)}`);
}

export function saveCyberProspectAnswers(id: string, answers: CyberAnswer[]): Promise<{ saved: number }> {
  return requestJson<{ saved: number }>(`/prospects/${encodeURIComponent(id)}/answers`, {
    method: 'PUT',
    body: JSON.stringify({ answers }),
  });
}

export function saveCyberProposalDraft(id: string, selectedServiceCodes: string[], options?: { proposalMode?: 'services' | 'budget'; targetBudget?: number; budgetPeriod?: 'monthly' | 'annual'; contractTermMonths?: number; proposalScenario?: 'essential' | 'recommended' | 'complete'; proposalCustomName?: string }): Promise<CyberGeneratedProposal> {
  return requestJson<CyberGeneratedProposal>(`/prospects/${encodeURIComponent(id)}/save-proposal-draft`, {
    method: 'POST',
    body: JSON.stringify({ selectedServiceCodes, ...(options || {}) }),
  });
}

export function generateCyberProposal(id: string, selectedServiceCodes: string[], options?: { proposalMode?: 'services' | 'budget'; targetBudget?: number; budgetPeriod?: 'monthly' | 'annual'; contractTermMonths?: number; proposalScenario?: 'essential' | 'recommended' | 'complete'; proposalCustomName?: string; draftProposalId?: string }): Promise<CyberGeneratedProposal> {
  return requestJson<CyberGeneratedProposal>(`/prospects/${encodeURIComponent(id)}/generate-proposal`, {
    method: 'POST',
    body: JSON.stringify({ selectedServiceCodes, ...(options || {}) }),
  });
}

export function updateCyberProspect(id: string, input: Partial<CyberProspectInput> & { status?: string }): Promise<CyberProspect> {
  return requestJson<CyberProspect>(`/prospects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function updateCyberProspectStatus(id: string, status: string): Promise<CyberProspect> {
  return updateCyberProspect(id, { status });
}

export function deleteCyberProspect(id: string): Promise<{ deleted: boolean; prospect: CyberProspect }> {
  return requestJson<{ deleted: boolean; prospect: CyberProspect }>(`/prospects/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function getCyberQuestions(): Promise<CyberQuestion[]> {
  return requestJson<CyberQuestion[]>('/questions');
}

export function getCyberServices(): Promise<CyberService[]> {
  return requestJson<CyberService[]>('/services');
}

export function getCyberProposals(): Promise<CyberProposal[]> {
  return requestJson<CyberProposal[]>('/proposals');
}

export function getCyberProposalDetail(id: string): Promise<CyberGeneratedProposal> {
  return requestJson<CyberGeneratedProposal>(`/proposals/${encodeURIComponent(id)}`);
}

export function applyCyberProposalDiscount(id: string, payload: { discountType?: string; percent?: number; amount?: number; reason?: string }): Promise<CyberGeneratedProposal> {
  return requestJson<CyberGeneratedProposal>(`/proposals/${encodeURIComponent(id)}/discounts`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function removeCyberProposalDiscount(proposalId: string, discountId: string): Promise<CyberGeneratedProposal> {
  return requestJson<CyberGeneratedProposal>(`/proposals/${encodeURIComponent(proposalId)}/discounts/${encodeURIComponent(discountId)}`, {
    method: 'DELETE',
  });
}

export function updateCyberProposalLineItem(proposalId: string, lineItemId: string, selected: boolean): Promise<CyberGeneratedProposal> {
  return requestJson<CyberGeneratedProposal>(`/proposals/${encodeURIComponent(proposalId)}/line-items/${encodeURIComponent(lineItemId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ selected }),
  });
}

export function updateCyberProposalStatus(id: string, status: string, justification?: string): Promise<CyberGeneratedProposal> {
  return requestJson<CyberGeneratedProposal>(`/proposals/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, justification }),
  });
}

export function generateCyberDeliveryBriefing(id: string): Promise<CyberProposalOutput> {
  return requestJson<CyberProposalOutput>(`/proposals/${encodeURIComponent(id)}/delivery-briefing`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function exportCyberClientVisual(id: string, options?: { includePrices?: boolean; titleSuffix?: string }): Promise<CyberProposalOutput> {
  return requestJson<CyberProposalOutput>(`/proposals/${encodeURIComponent(id)}/export-client-visual`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
}

export function sendCyberOfferEmail(id: string): Promise<CyberOfferEmailResult> {
  return requestJson<CyberOfferEmailResult>(`/proposals/${encodeURIComponent(id)}/send-offer-email`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function createCyberProposal(prospectId: string): Promise<CyberProposal> {
  return requestJson<CyberProposal>('/proposals', {
    method: 'POST',
    body: JSON.stringify({ prospectId }),
  });
}

export function getCyberAdminSnapshot(): Promise<CyberAdminSnapshot> {
  return requestJson<CyberAdminSnapshot>('/admin');
}

export function saveCyberAdminEntity(path: string, payload: Record<string, unknown>): Promise<unknown> {
  return requestJson<unknown>(`/admin/${path}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function publishCyberAdminVersion(payload: { parameterSetId?: string; ruleVersionId?: string }): Promise<CyberAdminSnapshot> {
  return requestJson<CyberAdminSnapshot>('/admin/publish', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
